import classNames from 'classnames';
import Link from 'next/link';
import React, { useContext } from 'react';
import { Views } from 'src/components/Views';
import { LIST_BY_RUBRICS } from 'src/constants/routes';
import { getDate } from 'src/helpers/news';
import { IDictionaryDto } from 'src/interfaces/news';
import { createLinkProps } from 'src/utils/routing';

import { Avatar, Icon, Space, Typography } from '@sravni/react-design-system';
import { Message, ThumbUp } from '@sravni/react-icons';

import { AdvPopUp } from '../../MaterialContent/components/AdvPopUp';

import styles from './styles.module.scss';

interface IProps {
  size?: 'small' | 'normal';
  color?: 'dark' | 'light';
  direction?: 'horizontal' | 'vertical' | 'vertical-reverse' | 'horizontal-reverse';
}

const context = React.createContext<IProps>({ size: 'normal', color: 'light', direction: 'vertical' });

const Image: React.FC<{
  src: string;
  lazy?: boolean;
  alt?: string;
  width?: string | number;
  height?: string | number;
  shieldText?: string;
  className?: string;
}> = ({ width, height, alt, src, lazy = true, shieldText, className }) => (
  <div className={classNames(styles.imageWrapper, className)}>
    {shieldText && <AdvPopUp title={shieldText} />}
    {lazy ? (
      <img alt={alt} className="lazy-load" data-src={src} width={width} height={height} />
    ) : (
      // @ts-ignore
      <img alt={alt} src={picture?.url || miniPicture} />
    )}
  </div>
);

const Content: React.FC<React.ComponentProps<typeof Space>> = ({ className, children, ...props }) => {
  return (
    <Space
      direction={'vertical'}
      justify={'space-between'}
      size={12}
      className={classNames(styles.descriptionWrapper, className)}
      {...props}
    >
      {children}
    </Space>
  );
};

const Row: React.FC<React.ComponentProps<typeof Space>> = ({ children, className, ...props }) => {
  return (
    <Space direction={'horizontal'} size={12} className={classNames(styles.row, className)} {...props}>
      {children}
    </Space>
  );
};

const Title: React.FC<Partial<React.ComponentProps<typeof Typography.Heading>>> = ({
  children,
  className,
  ...props
}) => {
  const { size, color } = useContext(context);

  return (
    <Typography.Heading
      level={size === 'small' ? 5 : 4}
      dangerouslySetInnerHTML={{ __html: children as string }}
      className={classNames(color === 'dark' ? 'h-color-L100' : 'h-color-D100', className)}
      {...props}
    />
  );
};

const Rubric: React.FC<{ rubric: IDictionaryDto } & React.ComponentProps<typeof Typography.Text>> = ({
  rubric,
  className,
  ...props
}) => {
  const rubricLink = createLinkProps('list', LIST_BY_RUBRICS, {
    rubric: rubric.alias,
  });

  return (
    <Typography.Text size={12} className={classNames(styles.text, className)} {...props}>
      <Link href={rubricLink.href} as={rubricLink.as}>
        <div className={styles.rubric}>
          <Typography.Link href={rubricLink.as} className={styles.link}>
            {rubric.name}
          </Typography.Link>
        </div>
      </Link>
    </Typography.Text>
  );
};

const Tag: React.FC<React.ComponentProps<typeof Typography.Text>> = ({ children, className, ...props }) => {
  return (
    <Typography.Text size={12} className={classNames(className, 'h-color-D20')} {...props}>
      {children}
    </Typography.Text>
  );
};

const DateComponent = ({
  children,
  className,
  ...props
}: { children: string } & React.ComponentProps<typeof Typography.Text>) => {
  return (
    <Typography.Text className={classNames(styles.text, className)} {...props}>
      {getDate(children)}
    </Typography.Text>
  );
};

const DescriptionRow: React.FC<
  {
    date: string;
    comments?: number;
    canCreateNewComments?: boolean;
    likes?: number;
    className?: string;
    visits?: number;
  } & Pick<React.ComponentProps<typeof Space>, 'justify'>
> = ({ date, comments, canCreateNewComments, likes, justify = 'space-between', className, visits }) => {
  return (
    <Space className={classNames(className, styles.descriptionInfo)} justify={justify} align={'center'}>
      <DateComponent>{date}</DateComponent>
      <Space justify={justify === 'start' ? 'start' : 'end'} align={'center'} size={8}>
        {visits > 0 && (
          <Views className={styles.infoBlock} size={12}>
            {visits}
          </Views>
        )}
        {comments > 0 && canCreateNewComments && (
          <div className={styles.infoBlock}>
            <Icon icon={<Message />} size={16} />
            <span>{comments}</span>
          </div>
        )}
        {likes > 0 && (
          <div className={styles.infoBlock}>
            <Icon icon={<ThumbUp />} size={16} />
            <span>{likes}</span>
          </div>
        )}
      </Space>
    </Space>
  );
};

const OrgName: React.FC<{ logotype: string; name: string } & React.ComponentProps<typeof Space>> = ({
  name,
  logotype,
  ...props
}) => {
  return (
    <Space size={4} {...props}>
      {logotype && <Avatar src={logotype} />}
      <Typography.Text>{name}</Typography.Text>
    </Space>
  );
};

export const Card: React.FC<IProps & Omit<React.AllHTMLAttributes<HTMLDivElement>, 'size'>> & {
  Content: typeof Content;
  Image: typeof Image;
  Row: typeof Row;
  Title: typeof Title;
  Rubric: typeof Rubric;
  DescriptionRow: typeof DescriptionRow;
  Date: typeof DateComponent;
  Tag: typeof Tag;
  OrgName: typeof OrgName;
} = ({ size, color, direction, children, className, ...props }) => {
  return (
    <context.Provider value={{ size, color, direction }}>
      <div className={classNames(styles.card, styles[`card__direction-${direction}`], className)} {...props}>
        {children}
      </div>
    </context.Provider>
  );
};

Card.Content = Content;
Card.Image = Image;
Card.Row = Row;
Card.Title = Title;
Card.Rubric = Rubric;
Card.DescriptionRow = DescriptionRow;
Card.Tag = Tag;
Card.Date = DateComponent;
Card.OrgName = OrgName;
